// frontend/src/components/Common/UI/fields/ImageField.jsx
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CardMedia,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import BackupIcon from '@mui/icons-material/Backup';
import CloseIcon from '@mui/icons-material/Close';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const ImageField = ({ disabled, editable, url, onChange, label }) => {
  const fileInputRef = useRef();
  const [preview, setPreview] = useState('');
  const [openFullscreen, setOpenFullscreen] = useState(false);

  useEffect(() => {
    if (url instanceof File) {
      const objectUrl = URL.createObjectURL(url);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }

    if (typeof url !== 'string' || !url.trim()) {
      setPreview('');
      return;
    }

    let cleaned = url
      .replace(/\\/g, '/')
      .replace(/^\/?uploads\//i, '')
      .replace(/^\/+/, '');

    let normalizedUrl =
      cleaned.startsWith('http') || cleaned.startsWith('//')
        ? cleaned
        : `${window.location.origin}/uploads/${cleaned}`;

    setPreview(normalizedUrl);
  }, [url]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    onChange(file);
    e.target.value = '';
  };

  const displayUrl = preview;

  const handleOpenFullscreen = () => {
    if (displayUrl) setOpenFullscreen(true);
  };

  const handleCloseFullscreen = () => setOpenFullscreen(false);

  return (
    <Box sx={{ width: '100%' }}>
      {!disabled && (
        <Typography variant='subtitle2' gutterBottom>
          {label || 'Изображение'}
        </Typography>
      )}

      <Grid container spacing={2}>
        {/* <Stack direction='row' spacing={3} alignItems='flex-start'> */}
        {/* Превью — кликабельное */}
        <Grid item xs={12} md={6}>
          <Box
            onClick={handleOpenFullscreen}
            sx={{
              height: 200,
              width: '100%',
              maxWidth: 320,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: displayUrl ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '&:hover': displayUrl
                ? { borderColor: 'primary.main', boxShadow: 3 }
                : {},
            }}
          >
            {displayUrl ? (
              <CardMedia
                component='img'
                image={displayUrl}
                alt='Превью изображения'
                sx={{
                  maxHeight: '100%',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                }}
                onError={(e) => {
                  console.error('Ошибка загрузки превью:', displayUrl);
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <Stack
                height='100%'
                alignItems='center'
                justifyContent='center'
                spacing={1}
                color='text.secondary'
              >
                <BackupIcon fontSize='large' />
                <Typography variant='body2'>Изображение не выбрано</Typography>
              </Stack>
            )}
          </Box>
        </Grid>

        {/* Управление */}
        {!disabled && editable && (
          <Grid item xs={12} md={6}>
            <Box>
              <input
                ref={fileInputRef}
                type='file'
                accept='image/jpeg,image/png,image/gif,image/webp'
                hidden
                onChange={handleFileSelect}
                disabled={disabled || !editable}
              />
              <Button
                variant='outlined'
                startIcon={<BackupIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || !editable}
              >
                Выбрать изображение
              </Button>

              {/* {displayUrl && (
                <Typography
                  variant='caption'
                  sx={{ mt: 1, display: 'block', color: 'text.secondary' }}
                >
                  {displayUrl.startsWith('blob:')
                    ? 'Новое изображение (локальное превью)'
                    : `Текущее: ${displayUrl.split('/').pop() || 'файл'}`}
                </Typography>
              )} */}
            </Box>
          </Grid>
        )}
        {/* </Stack> */}
      </Grid>

      {/* Fullscreen Modal с зумом */}
      <Dialog
        open={openFullscreen}
        onClose={handleCloseFullscreen}
        maxWidth='xl'
        fullWidth
        fullScreen // на мобильных будет полноэкранным автоматически
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'black',
            m: 0,
            p: 0,
          },
        }}
      >
        <IconButton
          onClick={handleCloseFullscreen}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            zIndex: 10,
            bgcolor: 'rgba(0,0,0,0.5)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          onClick={handleCloseFullscreen} // ← клик по фону → закрыть
          sx={{
            p: 0,
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer', // визуальный намёк
            overflow: 'hidden',
          }}
        >
          <TransformWrapper
            initialScale={1}
            initialPositionX={0}
            initialPositionY={0}
            wheel={{ step: 0.1 }}
            doubleClick={{ disabled: false }}
            pinch={{ disabled: false }}
            // добавьте ограничения:
            minScale={0.1}
            maxScale={5}
            limitToBounds={true} // ← ключевое! ограничивает выход за границы
            // centerOnInit={true}
            // ← КРИТИЧНО:
            centerOnInit={false} // отключаем автоцентрирование
            disablePadding={true} // отключаем внутренние отступы
          >
            <TransformComponent
              wrapperStyle={{
                width: '100vw', // ← используем vw вместо %
                height: '100vh',
                display: 'flex !important',
                alignItems: 'center !important',
                justifyContent: 'center !important',
                // width: '100%',
                // height: '100%',
                // display: 'flex',
                // alignItems: 'center',
                // justifyContent: 'center',
              }}
              contentStyle={{
                // display: 'flex',
                // alignItems: 'center',
                // justifyContent: 'center',
                // width: 'auto', // ← авто-размер для контента
                // height: 'auto',

                display: 'flex !important',
                alignItems: 'center !important',
                justifyContent: 'center !important',
                transform: 'none !important', // сбрасываем transform библиотеки
              }}
            >
              <img
                src={displayUrl}
                alt='Полноэкранное изображение'
                style={{
                  // maxWidth: '100%',
                  // maxHeight: '100%',
                  maxWidth: '100vw',
                  maxHeight: '100vh',
                  objectFit: 'contain',
                  touchAction: 'none', // важно для мобильного зума
                }}
                draggable={false}
                onClick={(e) => {
                  e.stopPropagation(); // ← самый важный момент!
                  // НЕ закрываем здесь — пусть закрывает фон
                }}
              />
            </TransformComponent>
          </TransformWrapper>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ImageField;

// import { useEffect, useRef, useState } from 'react';
// import { Box, Button, CardMedia, Stack, Typography } from '@mui/material';
// import BackupIcon from '@mui/icons-material/Backup';
// import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

// const ImageField = ({ disabled, editable, url, onChange, label }) => {
//   const fileInputRef = useRef();
//   const [preview, setPreview] = useState(url || '');

//   useEffect(() => {
//     // console.log('[ImageField] Исходный пропс url:', url);
//     // console.log('[ImageField] typeof url:', typeof url);

//     if (url instanceof File) {
//       const objectUrl = URL.createObjectURL(url);
//       //   console.log('[ImageField] Blob URL для файла:', objectUrl);
//       setPreview(objectUrl);
//       return () => URL.revokeObjectURL(objectUrl);
//     }

//     if (typeof url !== 'string' || !url.trim()) {
//       //   console.log('[ImageField] url пустой → preview = ""');
//       setPreview('');
//       return;
//     }

//     // ← Ключевой фикс здесь
//     let cleaned = url
//       .replace(/\\/g, '/') // все \ → /
//       .replace(/^\/?uploads\//i, '') // убираем ведущий /uploads/ или uploads/ (регистронезависимо)
//       .replace(/^\/+/, ''); // убираем лишние ведущие слеши, если остались

//     let normalizedUrl;

//     if (
//       cleaned.startsWith('http://') ||
//       cleaned.startsWith('https://') ||
//       cleaned.startsWith('//')
//     ) {
//       normalizedUrl = cleaned; // уже полный URL — не трогаем
//       //   console.log('[ImageField] Уже абсолютный URL');
//     } else {
//       normalizedUrl = `${window.location.origin}/uploads/${cleaned}`;
//       //   console.log(
//       //     '[ImageField] Нормализован относительный путь →',
//       //     normalizedUrl,
//       //   );
//     }

//     setPreview(normalizedUrl);
//   }, [url]);

//   const handleFileSelect = (e) => {
//     const file = e.target.files?.[0];
//     if (!file || !file.type.startsWith('image/')) return;

//     onChange(file); // передаём File наверх
//     e.target.value = ''; // сброс для повторного выбора того же файла
//   };

//   const displayUrl = preview;

//   return (
//     <Box sx={{ width: '100%' }}>
//       <Typography variant='subtitle2' gutterBottom>
//         {label || 'Изображение'}
//       </Typography>
//       <Stack direction='row' spacing={3} alignItems='flex-start'>
//         {/* Блок превью */}
//         <Box
//           sx={{
//             height: 200, // только высота фиксирована
//             width: '100%', // контейнер занимает всю доступную ширину родителя
//             maxWidth: 320, // но не шире 320px (опционально)
//             border: '1px dashed',
//             borderColor: 'divider',
//             borderRadius: 2,
//             overflow: 'hidden',
//             position: 'relative',
//             display: 'flex', // важно для центрирования
//             alignItems: 'center',
//             justifyContent: 'center',
//             // bgcolor: 'grey.50', // опционально — фон, если изображение меньше
//           }}
//         >
//           {displayUrl ? (
//             <CardMedia
//               component='img'
//               image={displayUrl} // ← здесь всегда строка (blob или http)
//               alt='Превью изображения'
//               sx={{
//                 maxHeight: '100%', // не выше контейнера
//                 maxWidth: '100%', // не шире контейнера
//                 width: 'auto', // ← сохраняем пропорции
//                 height: 'auto', // ← сохраняем пропорции
//                 objectFit: 'contain', // или 'cover' — см. ниже разницу
//               }}
//               onError={(e) => {
//                 console.error('Ошибка загрузки превью:', displayUrl);
//                 e.target.style.display = 'none'; // скрываем сломанный img
//               }}
//             />
//           ) : (
//             <Stack
//               height='100%'
//               alignItems='center'
//               justifyContent='center'
//               spacing={1}
//               color='text.secondary'
//             >
//               <BackupIcon fontSize='large' />
//               <Typography variant='body2'>Изображение не выбрано</Typography>
//             </Stack>
//           )}
//         </Box>

//         {/* Управление */}
//         {editable && (
//           <Box>
//             <input
//               ref={fileInputRef}
//               type='file'
//               accept='image/jpeg,image/png,image/gif,image/webp'
//               hidden
//               onChange={handleFileSelect}
//               disabled={disabled || !editable}
//             />

//             <Button
//               variant='outlined'
//               startIcon={<BackupIcon />}
//               onClick={() => fileInputRef.current?.click()}
//               disabled={disabled || !editable}
//             >
//               Выбрать изображение
//             </Button>

//             {displayUrl && (
//               <Typography
//                 variant='caption'
//                 sx={{ mt: 1, display: 'block', color: 'text.secondary' }}
//               >
//                 {displayUrl.startsWith('blob:')
//                   ? 'Новое изображение (локальное превью)'
//                   : `Текущее: ${displayUrl.split('/').pop() || 'файл'}`}
//               </Typography>
//             )}
//           </Box>
//         )}
//       </Stack>
//     </Box>
//   );
// };

// export default ImageField;

// // objectFit,Поведение,Когда использовать
// // contain,"Полностью показывает всё изображение, добавляет пустое пространство",Хочешь видеть всё фото без обрезки
// // cover,"Заполняет весь контейнер, обрезает края если пропорции не совпадают","Хочешь, чтобы превью всегда было заполнено"
// // scale-down,Показывает полностью или уменьшает до размеров контейнера,"Редко, промежуточный вариант"
